import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from 'react';
import Header from '../Components/Header/Header';
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';
export default function DatabaseViewer() {
  const {
    t
  } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tables, setTables] = useState({});
  const [selectedTable, setSelectedTable] = useState('_User');
  const [tableData, setTableData] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending'
  }); // prevent repeated default selections

  const hasSelectedDefault = useRef(false); // Local visibility policy to compute allowed properties per card in admin view

  const CARD_POLICY = {
    Personal: ['givenName', 'familyName', 'emailAddresses', 'phoneNumbers', 'birthday', 'address', 'notes'],
    Work: ['givenName', 'familyName', 'emailAddresses', 'phoneNumbers', 'jobTitle', 'company', 'department', 'workAddress', 'workPhone', 'linkedinProfile'],
    Public: ['givenName', 'familyName'],
    Family: ['givenName', 'familyName', 'emailAddresses', 'phoneNumbers', 'birthday', 'address', 'emergencyContact', 'medicalInfo'],
    Friends: ['givenName', 'familyName', 'emailAddresses', 'phoneNumbers', 'birthday', 'socialMediaProfiles']
  };

  const getAllowedPropertiesForCard = cardName => {
    if (!cardName) return [];
    return CARD_POLICY[cardName] || [];
  }; // Check database connection


  useEffect(() => {
    fetch(`${API_BASE}/status`).then(res => res.json()).then(data => {
      setConnectionStatus(data.success ? 'connected' : 'error');
    }).catch(() => setConnectionStatus('error'));
  }, []); // Load all tables

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetch(`${API_BASE}/database/tables`).then(res => res.ok ? res : Promise.reject(res)).then(res => res.json()).then(data => {
      if (!mounted) return;
      setTables(data); // Single delayed attempt to avoid rate-limit bursts

      try {
        if (!hasSelectedDefault.current && (!selectedTable || selectedTable === '_User') && data && Object.prototype.hasOwnProperty.call(data, '_User')) {
          setTimeout(() => {
            if (!hasSelectedDefault.current) {
              hasSelectedDefault.current = true;
              loadTableData('_User');
            }
          }, 1000);
        }
      } catch (_) {}
    }).catch(err => {
      if (!mounted) return;
      setError(typeof err === 'string' ? err : err.statusText || 'Failed to load database tables');
    }).finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTable]); // Load specific table data

  const loadTableData = (tableName, limit = 3000, offset = 0) => {
    setSelectedTable(tableName);
    setTableData(null);
    setSortConfig({
      key: null,
      direction: 'ascending'
    });
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString()
    }); // Fetch table data

    fetch(`${API_BASE}/database/tables/${tableName}?${params}`).then(res => res.ok ? res : Promise.reject(res)).then(res => res.json()).then(data => {
      // If viewing Card table, augment rows with allowedProperties column for admin
      if (tableName === 'Circle' && data && Array.isArray(data.data)) {
        // For each card row, fetch filtered property values from server
        Promise.all(data.data.map(async row => {
          try {
            const resp = await fetch(`${API_BASE}/database/cards/${row.id}/properties`);
            if (!resp.ok) throw new Error(`${resp.status}`);
            const json = await resp.json();
            const properties = json && json.data && Array.isArray(json.data.properties) ? json.data.properties : [];
            return { ...row,
              allowedProperties: getAllowedPropertiesForCard(row.name),
              propertyValues: properties
            };
          } catch (_) {
            return { ...row,
              allowedProperties: getAllowedPropertiesForCard(row.name),
              propertyValues: []
            };
          }
        })).then(rowsWithProps => setTableData({ ...data,
          data: rowsWithProps
        }));
      } else {
        setTableData(data);
      }
    }).catch(err => {
      setError(`Failed to load table ${tableName}: ${err.message}`);
    });
  }; // remove prior aggressive auto-load effects to avoid 429s
  // Global search across all tables


  const performGlobalSearch = () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    setSearchResults(null);
    fetch(`${API_BASE}/database/search?q=${encodeURIComponent(searchQuery.trim())}&limit=3000`).then(res => res.ok ? res : Promise.reject(res)).then(res => res.json()).then(data => {
      setSearchResults(data);
    }).catch(err => {
      setError(`Search failed: ${err.message}`);
    }).finally(() => setSearchLoading(false));
  }; // Handle edit record


  const handleEditRecord = (record, index) => {
    setEditRecord(record);
    setEditOpen(true);
  }; // Handle delete record


  const handleDeleteRecord = async (record, index) => {
    // Find the ID field (could be 'id', 'objectId', or other variations)
    const idField = record.id || record.objectId || record._id || Object.keys(record).find(key => key.toLowerCase().includes('id'));
    const recordId = record[idField] || record.id || record.objectId || record._id;

    if (!recordId) {
      window.alert('Cannot delete: No ID field found in record');
      return;
    } // Confirm deletion


    if (!window.confirm(`Are you sure you want to delete this record from ${selectedTable}?\n\nID: ${recordId}\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      console.log('Deleting record:', {
        table: selectedTable,
        id: recordId
      });
      const resp = await fetch(`${API_BASE}/database/tables/${selectedTable}/${recordId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        console.error('API Error:', resp.status, errorText);
        throw new Error(`Delete failed: ${resp.status} ${errorText}`);
      } // Reload table data to reflect changes


      loadTableData(selectedTable);
    } catch (e) {
      console.error('Delete error:', e);
      window.alert(`Failed to delete record: ${e.message}`);
    }
  };

  const renderGlobalSearch = () => {
    return <div style={{
      marginBottom: 20
    }}>
      <h3 style={{
        color: '#212529',
        marginBottom: 10
      }}>{t("Global Search")}</h3>
      <div style={{
        display: 'flex',
        gap: 10,
        marginBottom: 10
      }}>
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={t("Search across all tables...")} style={{
          flex: 1,
          padding: '10px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          fontSize: '14px'
        }} onKeyPress={e => e.key === 'Enter' && performGlobalSearch()} />
        <button onClick={performGlobalSearch} disabled={searchLoading || !searchQuery.trim()} style={{
          padding: '10px 20px',
          background: '#007bff',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: searchLoading ? 'not-allowed' : 'pointer',
          opacity: searchLoading ? 0.6 : 1
        }}>
          {searchLoading ? 'Searching...' : 'Search'}
        </button>
        <button onClick={() => {
          setSearchQuery('');
          setSearchResults(null);
        }} disabled={searchLoading && !searchResults} style={{
          padding: '10px 20px',
          background: '#6c757d',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}>{t("Reset")}</button>
      </div>

      {searchResults && <div style={{
        marginTop: 20
      }}>
          <h4 style={{
          color: '#212529'
        }}>{t("Search Results (")}{searchResults.total}{t("results)")}</h4>
          <div style={{
          overflowX: 'auto',
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
            <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '12px'
          }}>
              <thead style={{
              position: 'sticky',
              top: 0,
              background: '#f8f9fa'
            }}>
                <tr>
                  <th style={{
                  textAlign: 'left',
                  borderBottom: '2px solid #ddd',
                  padding: '8px',
                  background: '#f8f9fa',
                  color: '#212529'
                }}>{t("Table")}</th>
                  {searchResults.results.length > 0 && Object.keys(searchResults.results[0]).filter(key => key !== '_table_name').map(col => <th key={col} style={{
                  textAlign: 'left',
                  borderBottom: '2px solid #ddd',
                  padding: '8px',
                  background: '#f8f9fa',
                  color: '#212529'
                }}>
                      {col}
                    </th>)}
                </tr>
              </thead>
              <tbody>
                {searchResults.results.map((row, index) => <tr key={index}>
                    <td style={{
                  borderBottom: '1px solid #f2f2f2',
                  padding: '8px',
                  fontWeight: 'bold'
                }}>
                      {row._table_name}
                    </td>
                    {Object.keys(row).filter(key => key !== '_table_name').map(col => {
                  const isParameters = col === 'parameters';
                  const style = {
                    borderBottom: '1px solid #f2f2f2',
                    padding: '8px',
                    ...(isParameters ? {
                      whiteSpace: 'pre-wrap',
                      textOverflow: 'unset',
                      overflow: 'auto',
                      maxWidth: 'none',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace'
                    } : {
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    })
                  };
                  const value = row[col];
                  return <td key={col} style={style}>
                          {typeof value === 'object' ? isParameters ? JSON.stringify(value, null, 2) : JSON.stringify(value) : String(value || '')}
                        </td>;
                })}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
  };

  const renderTableList = () => {
    return <div style={{
      padding: '10px 0',
      display: 'flex',
      gap: 12,
      alignItems: 'center'
    }}>
      <label style={{
        fontWeight: 700,
        color: '#2d3748'
      }}>{t("Table:")}</label>
      <select value={selectedTable} onChange={e => loadTableData(e.target.value)} style={{
        padding: '8px 12px',
        border: '1px solid #ddd',
        borderRadius: 6,
        minWidth: 240
      }}>
        {Object.keys(tables).map((tableName, index) => <option key={tableName} value={tableName}>
            {index + 1}. {tableName} ({tables[tableName]?.length || 0})
          </option>)}
      </select>
      <div style={{
        background: 'linear-gradient(135deg, #667eea, #764ba2)',
        color: '#fff',
        fontSize: '12px',
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: 12,
        minWidth: 24,
        textAlign: 'center'
      }}>
        {Object.keys(tables).length}
      </div>

    </div>;
  };

  const renderTableSchema = () => null;

  const renderTableData = () => {
    if (!tableData) return null;
    const {
      data
    } = tableData;
    if (!data || data.length === 0) return <p>{t("No data found in this table.")}</p>;
    let sortedData = [...data];

    if (sortConfig !== null && sortConfig.key) {
      sortedData.sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (valA < valB) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }

        if (valA > valB) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }

        return 0;
      });
    } // Ensure username shows first in the table


    const rawColumns = Object.keys(sortedData[0] || {});
    const columns = [...['username'].filter(c => rawColumns.includes(c)), ...rawColumns.filter(c => c !== 'username')];

    const requestSort = key => {
      let direction = 'ascending';

      if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
        direction = 'descending';
      }

      setSortConfig({
        key,
        direction
      });
    };

    return <div style={{
      marginBottom: 20
    }}>
        <div style={{
        overflowX: 'auto'
      }}>
          <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '12px',
          tableLayout: 'auto'
        }}>
            <thead style={{
            position: 'sticky',
            top: 0,
            background: '#f8f9fa'
          }}>
              <tr>
                <th style={{
                textAlign: 'center',
                borderBottom: '2px solid #ddd',
                padding: '8px',
                background: '#f8f9fa',
                color: '#000',
                fontWeight: 700,
                width: '50px'
              }}>
                  #
                </th>
                <th style={{
                textAlign: 'center',
                borderBottom: '2px solid #ddd',
                padding: '8px',
                background: '#f8f9fa',
                color: '#000',
                fontWeight: 700,
                width: '60px'
              }}>{t("Edit")}</th>
                <th style={{
                textAlign: 'center',
                borderBottom: '2px solid #ddd',
                padding: '8px',
                background: '#f8f9fa',
                color: '#000',
                fontWeight: 700,
                width: '60px'
              }}>{t("Delete")}</th>
                {columns.map(col => <th key={col} onClick={() => requestSort(col)} style={{
                textAlign: 'left',
                borderBottom: '2px solid #ddd',
                padding: '8px',
                background: '#f8f9fa',
                color: '#000',
                fontWeight: 700,
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                    {col} {sortConfig.key === col ? sortConfig.direction === 'ascending' ? '↑' : '↓' : ''}
                  </th>)}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, index) => <tr key={index}>
                  <td style={{
                borderBottom: '1px solid #f2f2f2',
                padding: '8px',
                textAlign: 'center',
                fontWeight: '600',
                color: '#6b7280',
                background: '#f9fafb'
              }}>
                    {index + 1}
                  </td>
                  <td style={{
                borderBottom: '1px solid #f2f2f2',
                padding: '8px',
                textAlign: 'center'
              }}>
                    <button onClick={() => handleEditRecord(row, index)} style={{
                  padding: '4px 8px',
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: '500'
                }} onMouseEnter={e => {
                  e.target.style.background = '#495057';
                }} onMouseLeave={e => {
                  e.target.style.background = '#6c757d';
                }}>{t("Edit")}</button>
                  </td>
                  <td style={{
                borderBottom: '1px solid #f2f2f2',
                padding: '8px',
                textAlign: 'center'
              }}>
                    <button onClick={() => handleDeleteRecord(row, index)} style={{
                  padding: '4px 8px',
                  background: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: '500'
                }} onMouseEnter={e => {
                  e.target.style.background = '#c82333';
                }} onMouseLeave={e => {
                  e.target.style.background = '#dc3545';
                }}>{t("Delete")}</button>
                  </td>
                  {columns.map(col => {
                const isParameters = col === 'parameters';
                const cellStyle = isParameters ? {
                  borderBottom: '1px solid #f2f2f2',
                  padding: '8px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace'
                } : {
                  borderBottom: '1px solid #f2f2f2',
                  padding: '8px'
                };
                const value = row[col];
                return <td key={col} style={cellStyle}>
                        {typeof value === 'object' ? isParameters ? JSON.stringify(value, null, 2) : JSON.stringify(value) : String(value || '')}
                      </td>;
              })}
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>;
  }; // Modal for editing a record


  const renderEditModal = () => {
    if (!editOpen || !editRecord) return null; // Fields that should remain read-only (system fields)

    const readOnlyFields = ['createdAt', 'updatedAt', 'acl', 'id', 'objectId', '_id']; // Show all fields from the record, excluding read-only ones

    const displayFields = Object.keys(editRecord).filter(field => !readOnlyFields.includes(field)); // Show read-only fields separately

    const readOnlyDisplayFields = Object.keys(editRecord).filter(field => readOnlyFields.includes(field));
    return <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
        <div style={{
        background: '#fff',
        padding: 20,
        borderRadius: 8,
        width: '800px',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
          <h3 style={{
          marginTop: 0
        }}>{t("Edit Record -")}{selectedTable}</h3>
          <div style={{
          display: 'grid',
          gridTemplateColumns: '200px 1fr',
          gap: 12,
          alignItems: 'center'
        }}>
            {displayFields.map(field => {
            const isReadOnly = readOnlyFields.includes(field);
            const fieldValue = editRecord[field];
            const isObject = typeof fieldValue === 'object' && fieldValue !== null;
            const isArray = Array.isArray(fieldValue);
            const isBoolean = typeof fieldValue === 'boolean';
            const isNumber = typeof fieldValue === 'number';
            return <React.Fragment key={field}>
                  <label style={{
                fontWeight: 600,
                fontSize: '14px'
              }}>
                    {field}
                    {isReadOnly && <span style={{
                  color: '#6c757d',
                  fontSize: '12px',
                  marginLeft: '8px'
                }}>{t("(read-only)")}</span>}
                  </label>
                  {isBoolean ? <select value={fieldValue ? 'true' : 'false'} style={{
                padding: 8,
                border: '1px solid #ddd',
                borderRadius: 4,
                backgroundColor: isReadOnly ? '#f8f9fa' : '#fff',
                color: isReadOnly ? '#6c757d' : '#000'
              }} disabled={isReadOnly} onChange={isReadOnly ? undefined : e => {
                const val = e.target.value === 'true';
                setEditRecord(prev => ({ ...prev,
                  [field]: val
                }));
              }}>
                      <option value="true">{t("true")}</option>
                      <option value="false">{t("false")}</option>
                    </select> : isObject || isArray ? <textarea value={JSON.stringify(fieldValue, null, 2)} style={{
                padding: 8,
                border: '1px solid #ddd',
                borderRadius: 4,
                backgroundColor: isReadOnly ? '#f8f9fa' : '#fff',
                color: isReadOnly ? '#6c757d' : '#000',
                minHeight: '60px',
                fontFamily: 'monospace',
                fontSize: '12px'
              }} readOnly={isReadOnly} onChange={isReadOnly ? undefined : e => {
                try {
                  const val = JSON.parse(e.target.value);
                  setEditRecord(prev => ({ ...prev,
                    [field]: val
                  }));
                } catch (err) {
                  // Keep the text as-is if JSON is invalid
                  setEditRecord(prev => ({ ...prev,
                    [field]: e.target.value
                  }));
                }
              }} /> : <input type={isNumber ? 'number' : 'text'} value={fieldValue ?? ''} style={{
                padding: 8,
                border: '1px solid #ddd',
                borderRadius: 4,
                backgroundColor: isReadOnly ? '#f8f9fa' : '#fff',
                color: isReadOnly ? '#6c757d' : '#000'
              }} readOnly={isReadOnly} onChange={isReadOnly ? undefined : e => {
                const val = isNumber ? parseFloat(e.target.value) || 0 : e.target.value;
                setEditRecord(prev => ({ ...prev,
                  [field]: val
                }));
              }} />}
                </React.Fragment>;
          })}
          </div>

          {
          /* Read-only fields section */
        }
          {readOnlyDisplayFields.length > 0 && <div style={{
          marginTop: 20,
          paddingTop: 20,
          borderTop: '1px solid #eee'
        }}>
              <h4 style={{
            margin: '0 0 12px 0',
            color: '#6c757d',
            fontSize: '14px'
          }}>{t("System Fields (Read-Only)")}</h4>
              <div style={{
            display: 'grid',
            gridTemplateColumns: '200px 1fr',
            gap: 12,
            alignItems: 'center'
          }}>
                {readOnlyDisplayFields.map(field => {
              const fieldValue = editRecord[field];
              const isObject = typeof fieldValue === 'object' && fieldValue !== null;
              const isArray = Array.isArray(fieldValue);
              return <React.Fragment key={field}>
                      <label style={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#6c757d'
                }}>
                        {field}
                      </label>
                      {isObject || isArray ? <textarea value={JSON.stringify(fieldValue, null, 2)} style={{
                  padding: 8,
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  minHeight: '60px',
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }} readOnly /> : <input type="text" value={fieldValue ?? ''} style={{
                  padding: 8,
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d'
                }} readOnly />}
                    </React.Fragment>;
            })}
              </div>
            </div>}

          <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 16
        }}>
            <button onClick={() => setEditOpen(false)} style={{
            padding: '8px 12px',
            background: '#6c757d',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer'
          }}>{t("Cancel")}</button>
            <button onClick={async () => {
            try {
              // Find the ID field (could be 'id', 'objectId', or other variations)
              const idField = editRecord.id || editRecord.objectId || editRecord._id || Object.keys(editRecord).find(key => key.toLowerCase().includes('id'));
              const recordId = editRecord[idField] || editRecord.id || editRecord.objectId || editRecord._id;

              if (!recordId) {
                window.alert('Cannot save: No ID field found in record');
                return;
              }

              console.log('Updating record:', {
                table: selectedTable,
                id: recordId,
                data: editRecord
              });
              const resp = await fetch(`${API_BASE}/database/tables/${selectedTable}/${recordId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  data: editRecord
                })
              });

              if (!resp.ok) {
                const errorText = await resp.text();
                console.error('API Error:', resp.status, errorText);
                throw new Error(`Save failed: ${resp.status} ${errorText}`);
              }

              setEditOpen(false); // reload table data to reflect changes

              loadTableData(selectedTable);
            } catch (e) {
              console.error('Save error:', e);
              window.alert(`Failed to save changes: ${e.message}`);
            }
          }} style={{
            padding: '8px 12px',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer'
          }}>{t("Save")}</button>
          </div>
        </div>
      </div>;
  };

  return <>
      <Header />
      <div style={{
      padding: 20,
      maxWidth: '1400px',
      margin: '0 auto'
    }}>
        <h1>{t("Database Viewer")}</h1>

        {
        /* Connection Status */
      }
        <div style={{
        marginBottom: 20,
        padding: '10px',
        borderRadius: '4px',
        background: connectionStatus === 'connected' ? '#d4edda' : '#f8d7da',
        color: connectionStatus === 'connected' ? '#155724' : '#721c24',
        border: `1px solid ${connectionStatus === 'connected' ? '#c3e6cb' : '#f5c6cb'}`
      }}>
          <strong>{t("Database Status:")}</strong> {connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
        </div>

        {loading && <p>{t("Loading database tables...")}</p>}
        {error && <div style={{
        padding: '10px',
        background: '#f8d7da',
        color: '#721c24',
        border: '1px solid #f5c6cb',
        borderRadius: '4px',
        marginBottom: 20
      }}>
            {error}
          </div>}

        {!loading && !error && <div style={{
        display: 'block'
      }}>
            {renderTableList()}
            {renderGlobalSearch()}
            {selectedTable ? <>
                {renderTableSchema()}
                {renderTableData()}
              </> : null}
          </div>}
      </div>
      {renderEditModal()}
    </>;
}